import React from 'react';
import { OptionCard } from './ui';
import {
  IconUpload,
  IconMail,
  IconBolt,
  IconLock,
  IconPieChart,
  IconLayers,
  IconChevronRight,
  IconInboxScan,
  IconForwardEmail,
  IconGeneratorMailback,
} from './icons';

/**
 * HomeLayouts — three swappable home-screen templates.
 *
 * All three variants share the same hero (logo + title + subtitle) and
 * security footer; only the middle section (primary action layout)
 * differs. Pick the preset via `config.homeLayout`:
 *
 *  - `'actions'`   — three action cards (File / Email / Fetch)      [default]
 *  - `'asset-type'`— two primary tiles (Mutual Funds / Stocks)
 *  - `'unified'`   — big upload zone + secondary chips
 *
 * Each layout calls the same set of dispatch callbacks so feature flags
 * and sub-flows behave identically across variants — only the
 * presentation changes.
 */

export interface HomeDispatch {
  onPickFile: () => void;           // routes through broker picker if enabled
  onPickEmail: () => void;
  onPickFetch: () => void;
  onPickMutualFunds: () => void;    // preset CAMS_KFINTECH, go to FILE_UPLOAD
  onPickDemat: () => void;          // go to FILE_UPLOAD or broker picker
  onPickInboundEmail: () => void;   // jump to email mode on forward tab
  onPickGmail: () => void;          // jump to email mode on connect tab
  onFetchCdsl: () => void;          // jump to FETCH_HUB on CDSL tab
  onFetchKfintech: () => void;      // jump to FETCH_HUB on KFintech tab
}

export interface HomeLayoutFlags {
  emailAvailable: boolean;
  fetchAvailable: boolean;
  enableInbox: boolean;
  enableInboundEmail: boolean;
  enableCdslFetch: boolean;
  enableGenerator: boolean;
  allowMutualFunds: boolean;
  allowDemat: boolean;
}

export interface HomeLayoutProps extends HomeDispatch, HomeLayoutFlags {
  logoUrl: string;
  title: string;
  subtitle: string;
  /** Drop a file straight into the widget's upload pipeline. */
  onFileDropped?: (file: File) => void;
}

// ─── Shared pieces ────────────────────────────────────────────────────

const Hero: React.FC<{ logoUrl: string; title: string; subtitle: string }> = ({
  logoUrl,
  title,
  subtitle,
}) => (
  <div style={{ textAlign: 'center', marginBottom: 22 }}>
    <img
      src={logoUrl}
      alt=""
      style={{
        width: 52,
        height: 52,
        objectFit: 'contain',
        marginBottom: 14,
      }}
    />
    <h2
      style={{
        fontSize: 22,
        fontWeight: 700,
        margin: 0,
        color: 'var(--pc-color-text)',
        letterSpacing: '-0.01em',
      }}
    >
      {title}
    </h2>
    <p
      style={{
        color: 'var(--pc-color-text-muted)',
        marginTop: 6,
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {subtitle}
    </p>
  </div>
);

const SecurityFooter: React.FC = () => (
  <div
    style={{
      marginTop: 18,
      padding: '10px 14px',
      backgroundColor: 'var(--pc-color-surface-subtle)',
      borderRadius: 'var(--pc-radius-md)',
      border: '1px solid var(--pc-color-border)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}
  >
    <span style={{ color: 'var(--pc-color-text-muted)' }}>
      <IconLock size={14} />
    </span>
    <span
      style={{
        fontSize: 12,
        color: 'var(--pc-color-text-muted)',
        lineHeight: 1.5,
      }}
    >
      Parsed live in your browser. We never keep the file.
    </span>
  </div>
);

function fetchDescription(enableCdslFetch: boolean, enableGenerator: boolean) {
  if (enableCdslFetch && enableGenerator)
    return 'CDSL OTP for stocks, or KFintech/CAMS mailback for mutual funds';
  if (enableCdslFetch) return 'Fetch your full demat (CDSL + NSDL) via OTP';
  return 'Email me a fresh mutual fund CAS';
}

// ─── Layout 1: ACTIONS (default) ──────────────────────────────────────

const HomeActions: React.FC<HomeLayoutProps> = (props) => {
  const {
    onPickFile,
    onPickEmail,
    onPickFetch,
    emailAvailable,
    fetchAvailable,
    enableInbox,
    enableInboundEmail,
    enableCdslFetch,
    enableGenerator,
    logoUrl,
    title,
    subtitle,
  } = props;

  return (
    <div>
      <Hero logoUrl={logoUrl} title={title} subtitle={subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <OptionCard
          icon={<IconUpload size={20} />}
          title="I have my statement"
          description="Upload the PDF from your device"
          accent="primary"
          onClick={onPickFile}
        />

        {emailAvailable && (
          <OptionCard
            icon={<IconMail size={20} />}
            title="It's in my email"
            description={
              enableInbox && enableInboundEmail
                ? 'Find it in your inbox, or forward the email to us'
                : enableInbox
                ? 'We scan your inbox for CAS emails'
                : 'Forward your CAS email to a unique address we give you'
            }
            onClick={onPickEmail}
          />
        )}

        {fetchAvailable && (
          <OptionCard
            icon={<IconBolt size={20} />}
            title="Fetch it for me"
            description={fetchDescription(enableCdslFetch, enableGenerator)}
            onClick={onPickFetch}
          />
        )}
      </div>

      <SecurityFooter />
    </div>
  );
};

// ─── Layout 2: ASSET-TYPE (advisor-friendly) ──────────────────────────
//
// Two root tiles. Each routes to a method-picker screen (rendered by
// the parent widget) that surfaces every enabled import path.

const HomeAssetType: React.FC<HomeLayoutProps> = (props) => {
  const {
    onPickMutualFunds,
    onPickDemat,
    allowMutualFunds,
    allowDemat,
    logoUrl,
    title,
    subtitle,
  } = props;

  return (
    <div>
      <Hero logoUrl={logoUrl} title={title} subtitle={subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {allowMutualFunds && (
          <AssetTile
            icon={<IconPieChart size={22} />}
            title="Mutual funds"
            description="Every fund house, in one import"
            onClick={onPickMutualFunds}
          />
        )}
        {allowDemat && (
          <AssetTile
            icon={<IconLayers size={22} />}
            title="Stocks & demat"
            description="Any Indian broker"
            onClick={onPickDemat}
          />
        )}
      </div>

      <SecurityFooter />
    </div>
  );
};

// ─── Layout 3: UNIFIED (upload-first) ─────────────────────────────────

const HomeUnified: React.FC<HomeLayoutProps> = (props) => {
  const {
    onPickFile,
    onPickGmail,
    onPickInboundEmail,
    onFetchCdsl,
    onFetchKfintech,
    emailAvailable,
    fetchAvailable,
    enableInbox,
    enableInboundEmail,
    enableCdslFetch,
    enableGenerator,
    logoUrl,
    title,
    subtitle,
    onFileDropped,
  } = props;

  const [dragActive, setDragActive] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    // If a direct drop handler was provided, use it — otherwise fall back
    // to opening the full upload view.
    if (onFileDropped) onFileDropped(file);
    else onPickFile();
  };

  return (
    <div>
      <Hero logoUrl={logoUrl} title={title} subtitle={subtitle} />

      {/* Primary drop zone */}
      <button
        type="button"
        onClick={() => {
          if (onFileDropped) inputRef.current?.click();
          else onPickFile();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          width: '100%',
          padding: '32px 20px',
          backgroundColor: dragActive
            ? 'var(--pc-color-primary-soft)'
            : 'var(--pc-color-surface-subtle)',
          border: `2px dashed ${
            dragActive ? 'var(--pc-color-primary)' : 'var(--pc-color-border-strong)'
          }`,
          borderRadius: 'var(--pc-radius-lg)',
          cursor: 'pointer',
          textAlign: 'center',
          transition: 'background-color 0.15s, border-color 0.15s',
          color: 'var(--pc-color-text)',
          fontFamily: 'inherit',
          display: 'block',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: 'var(--pc-color-primary-soft)',
            color: 'var(--pc-color-primary)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 10,
          }}
        >
          <IconUpload size={22} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
          Drop your statement here
        </div>
        <div style={{ fontSize: 12, color: 'var(--pc-color-text-muted)' }}>
          or click to browse · mutual fund or demat CAS PDFs
        </div>
        {onFileDropped && (
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        )}
      </button>

      {/* Secondary quick-access chips */}
      {(emailAvailable || fetchAvailable) && (
        <div style={{ marginTop: 16 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'var(--pc-color-text-faded)',
              margin: '0 0 8px',
              textAlign: 'center',
            }}
          >
            Don't have a file?
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 8,
            }}
          >
            {enableInbox && (
              <SecondaryChip
                icon={<IconInboxScan size={14} />}
                label="Find in inbox"
                subtitle="Existing CAS emails"
                onClick={onPickGmail}
              />
            )}
            {enableInboundEmail && (
              <SecondaryChip
                icon={<IconForwardEmail size={14} />}
                label="Forward email"
                subtitle="From any inbox"
                onClick={onPickInboundEmail}
              />
            )}
            {enableCdslFetch && (
              <SecondaryChip
                icon={<IconBolt size={14} />}
                label="Fetch via CDSL OTP"
                subtitle="Full demat, in seconds"
                onClick={onFetchCdsl}
              />
            )}
            {enableGenerator && (
              <SecondaryChip
                icon={<IconGeneratorMailback size={14} />}
                label="Email me a fresh CAS"
                subtitle="KFintech/CAMS, ~2 mins"
                onClick={onFetchKfintech}
              />
            )}
          </div>
        </div>
      )}

      <SecurityFooter />
    </div>
  );
};

// ─── Small helpers — AssetTile and SecondaryChip ──────────────────────

const AssetTile: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ icon, title, description, onClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        padding: '18px 18px',
        borderRadius: 'var(--pc-radius-lg)',
        border: `1.5px solid ${
          hover ? 'var(--pc-color-primary)' : 'var(--pc-color-border)'
        }`,
        backgroundColor: hover
          ? 'var(--pc-color-primary-soft)'
          : 'var(--pc-color-surface)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        textAlign: 'left',
        fontFamily: 'inherit',
        color: 'var(--pc-color-text)',
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--pc-radius-md)',
          backgroundColor: 'var(--pc-color-primary-soft)',
          color: 'var(--pc-color-primary)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 600 }}>
          {title}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            marginTop: 2,
          }}
        >
          {description}
        </span>
      </span>
      <span style={{ color: 'var(--pc-color-text-faded)' }}>
        <IconChevronRight size={16} />
      </span>
    </button>
  );
};

const SecondaryChip: React.FC<{
  icon?: React.ReactNode;
  label: string;
  subtitle?: string;
  onClick: () => void;
}> = ({ icon, label, subtitle, onClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: subtitle ? '10px 12px' : '8px 14px',
        borderRadius: 'var(--pc-radius-md)',
        border: `1px solid ${
          hover ? 'var(--pc-color-primary)' : 'var(--pc-color-border)'
        }`,
        backgroundColor: hover
          ? 'var(--pc-color-primary-soft)'
          : 'var(--pc-color-surface)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 13,
        color: 'var(--pc-color-text)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        transition: 'background-color 0.15s, border-color 0.15s',
        textAlign: 'left',
      }}
    >
      {icon && (
        <span
          style={{
            display: 'inline-flex',
            color: 'var(--pc-color-primary)',
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600 }}>{label}</span>
        {subtitle && (
          <span
            style={{
              display: 'block',
              fontSize: 11,
              color: 'var(--pc-color-text-muted)',
              marginTop: 1,
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
    </button>
  );
};

// ─── Dispatcher ───────────────────────────────────────────────────────

export const HomeLayout: React.FC<
  HomeLayoutProps & { variant: 'actions' | 'asset-type' | 'unified' }
> = ({ variant, ...rest }) => {
  if (variant === 'asset-type') return <HomeAssetType {...rest} />;
  if (variant === 'unified') return <HomeUnified {...rest} />;
  return <HomeActions {...rest} />;
};
